import { useState, useEffect, useCallback } from 'react';
import { useGroupActions } from '@/hooks/useGroupActions';
import { useGroupStore } from '@/stores/useGroupStore';
import { useFriendStore } from '@/stores/useFriendStore';
import { GroupListItem, GroupMember, AddGroupParticipantsParams, RemoveGroupParticipantsParams, FriendInfo } from '@/api/types';
import { preventDuplicateClick } from '@/utils/my-utils';
import MessageManager from '@/utils/ably-im/message-manager';

interface GroupMembersPanelProps {
  group: GroupListItem;
  onBack: () => void;
}

/**
 * 群组成员管理面板
 * 展示、添加、移除群组成员
 */
export default function GroupMembersPanel({
  group,
  onBack
}: GroupMembersPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  // 移除成员时的选择
  const [selectedMembers, setSelectedMembers] = useState<GroupMember[]>([]);
  // 添加成员时的选择
  const [selectedFriends, setSelectedFriends] = useState<FriendInfo[]>([]);
  const [activeTab, setActiveTab] = useState<'members' | 'add'>('members');

  const { addParticipants, removeParticipants, isLoading: actionLoading, error: actionError } = useGroupActions();
  const {
    getGroupMembers,
  } = useGroupStore();
  const { friendList, getFriendList } = useFriendStore();

  const members = useGroupStore.getState().groupInfoMap.get(group.id)?.participants || [];

  // 加载群组成员和好友列表
  useEffect(() => {
    if (group.id) {
      getGroupMembers(group.id);
      getFriendList();
    }
  }, [group.id, getGroupMembers, getFriendList]);

  /**
   * 过滤成员列表
   */
  const filteredMembers = members.filter(member =>
    member.nickname.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * 过滤好友列表（排除已经是群成员的好友）
   */
  const memberIds = new Set(members.map(m => m.id));
  const availableFriends = friendList.filter(friend => !memberIds.has(friend.id));
  const filteredFriends = availableFriends.filter(friend =>
    friend.nickname.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (friend.alias && friend.alias.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  /**
   * 切换成员选择
   */
  const toggleMemberSelection = (member: GroupMember) => {
    setSelectedMembers(prev =>
      prev.includes(member)
        ? prev.filter(it => it.id !== member.id)
        : [...prev, member]
    );
  };

  /**
   * 切换好友选择
   */
  const toggleFriendSelection = (friend: FriendInfo) => {
    setSelectedFriends(prev =>
      prev.includes(friend)
        ? prev.filter(it => it.id !== friend.id)
        : [...prev, friend]
    );
  };

  /**
   * 处理添加成员
   */
  const handleAddMembers = useCallback(
    preventDuplicateClick(async () => {
      if (selectedFriends.length === 0) return;

      const params: AddGroupParticipantsParams = {
        participant_ids: selectedFriends.map(friend => friend.id)
      };

      const success = await addParticipants(group.id, params);
      if (success) {
        // 刷新成员列表
        await getGroupMembers(group.id);
        setSelectedFriends([]);
        setActiveTab('members');

        const conversationId = group.conversation_id;
        const memberHandles = selectedFriends.map(friend => friend.handle);
        const memberNames = selectedFriends.map(friend => friend.nickname);
        MessageManager.getInstance().sendGroupMemberAddedNotification(conversationId, memberHandles, memberNames);
      }
    }),
    [selectedFriends, addParticipants, group.id, getGroupMembers]
  );

  /**
   * 处理移除成员
   */
  const handleRemoveMembers = useCallback(
    preventDuplicateClick(async () => {
      if (selectedMembers.length === 0) return;

      const confirmed = confirm(`确定要移除这 ${selectedMembers.length} 位成员吗？`);
      if (!confirmed) return;

      const params: RemoveGroupParticipantsParams = {
        participant_ids: selectedMembers.map(member => member.id)
      };

      const success = await removeParticipants(group.id, params);
      if (success) {
        // 更新本地状态
        await getGroupMembers(group.id);
        setSelectedMembers([]);

        const conversationId = group.conversation_id;
        const memberHandles = selectedMembers.map(member => member.handle);
        const memberNames = selectedMembers.map(member => member.nickname);
        MessageManager.getInstance().sendGroupMemberRemovedNotification(conversationId, memberHandles, memberNames);
      }
    }),
    [selectedMembers, removeParticipants, group.id]
  );

  /**
   * 渲染成员项
   */
  const renderMemberItem = (member: GroupMember) => {
    const isSelected = selectedMembers.includes(member);
    const isOwner = member.role === 'owner';

    return (
      <div
        key={member.id}
        onClick={() => !isOwner && toggleMemberSelection(member)}
        className={`flex items-center p-3 rounded-lg transition-colors ${isOwner
          ? 'bg-yellow-50 border border-yellow-200'
          : isSelected
            ? 'bg-red-50 border-2 border-red-200 cursor-pointer'
            : 'hover:bg-gray-50 border border-transparent cursor-pointer'
          }`}
      >
        {/* 选择框（群主不能移除） */}
        {!isOwner && (
          <div className="flex-shrink-0 mr-3">
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
              ? 'bg-red-600 border-red-600'
              : 'border-gray-300'
              }`}>
              {isSelected && (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
        )}

        {/* 头像 */}
        <div className="flex-shrink-0 mr-3">
          {member.avatar ? (
            <img
              src={member.avatar}
              alt={member.nickname}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-600 font-medium">
                {member.nickname?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>

        {/* 成员信息 */}
        <div className="flex-1">
          <div className="flex items-center">
            <p className="text-sm font-medium text-gray-900">{member.nickname}</p>
            {member.role === 'owner' && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium text-yellow-800 bg-yellow-100 rounded">
                群主
              </span>
            )}
            {member.role === 'admin' && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded">
                管理员
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">ID: {member.handle}</p>
        </div>
      </div>
    );
  };

  /**
   * 渲染好友项
   */
  const renderFriendItem = (friend: FriendInfo) => {
    const isSelected = selectedFriends.includes(friend);

    return (
      <div
        key={friend.id}
        onClick={() => toggleFriendSelection(friend)}
        className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${isSelected
          ? 'bg-green-50 border-2 border-green-200'
          : 'hover:bg-gray-50 border border-transparent'
          }`}
      >
        {/* 选择框 */}
        <div className="flex-shrink-0 mr-3">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
            ? 'bg-green-600 border-green-600'
            : 'border-gray-300'
            }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>

        {/* 头像 */}
        <div className="flex-shrink-0 mr-3">
          {friend.avatar ? (
            <img
              src={friend.avatar}
              alt={friend.nickname}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-600 font-medium">
                {friend.nickname?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>

        {/* 好友信息 */}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {friend.alias || friend.nickname}
          </p>
          {friend.alias && friend.nickname !== friend.alias && (
            <p className="text-xs text-gray-500">{friend.nickname}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-gray-900">群组成员</h3>
        </div>
      </div>

      {/* 标签页切换 */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('members')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${activeTab === 'members'
            ? 'text-purple-600 border-b-2 border-purple-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          成员列表 ({members.length})
        </button>
        <button
          onClick={() => setActiveTab('add')}
          className={`flex-1 py-3 px-4 text-sm font-medium ${activeTab === 'add'
            ? 'text-purple-600 border-b-2 border-purple-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          添加成员
        </button>
      </div>

      {/* 搜索框 */}
      <div className="p-4 border-b border-gray-200">
        <input
          type="text"
          placeholder={activeTab === 'members' ? '搜索成员...' : '搜索好友...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* 操作按钮 */}
      {((activeTab === 'members' && selectedMembers.length > 0) ||
        (activeTab === 'add' && selectedFriends.length > 0)) && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            {activeTab === 'members' ? (
              <button
                onClick={handleRemoveMembers}
                disabled={actionLoading}
                className="w-full py-2 px-4 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 rounded-lg transition-colors"
              >
                {actionLoading ? '移除中...' : `移除选中的 ${selectedMembers.length} 位成员`}
              </button>
            ) : (
              <button
                onClick={handleAddMembers}
                disabled={actionLoading}
                className="w-full py-2 px-4 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-lg transition-colors"
              >
                {actionLoading ? '添加中...' : `添加选中的 ${selectedFriends.length} 位好友`}
              </button>
            )}
          </div>
        )}

      {/* 内容区域 */}
      <div className="flex-1 overflow-y-auto">
        {members.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-sm text-gray-500">加载中...</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {activeTab === 'members' ? (
              filteredMembers.length > 0 ? (
                filteredMembers.map(renderMemberItem)
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">
                    {searchQuery ? '未找到匹配的成员' : '暂无成员'}
                  </p>
                </div>
              )
            ) : (
              filteredFriends.length > 0 ? (
                filteredFriends.map(renderFriendItem)
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">
                    {searchQuery ? '未找到匹配的好友' : '暂无可添加的好友'}
                  </p>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* 错误信息 */}
      {actionError && (
        <div className="p-4 border-t border-gray-200">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{actionError}</p>
          </div>
        </div>
      )}
    </div>
  );
}
